import React, { useCallback } from 'react';
import { Box } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import TuneIcon from '@mui/icons-material/Tune';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import { useTranslation } from 'react-i18next';
import { useLocalParticipant } from '@livekit/components-react';

import { reportError } from '../../../lib/sentry';
import { isMediaDeviceError } from '../callSignals';
import { DS, controlButtonSx, outlinedControlButtonSx } from '../designTokens';
import DeviceMenuButton from './DeviceMenuButton';

const reportToggleError = (err: unknown) => {
  // DeviceFailedBanner already reports a device that will not start.
  if (isMediaDeviceError(err)) return;
  reportError(err, { tags: { feature: 'video' } });
};

type ToggleProps = {
  icon: React.ReactNode;
  label: string;
  ariaLabel: string;
  isOff: boolean;
  onClick: () => void;
};

const ToggleButton = ({
  icon,
  label,
  ariaLabel,
  isOff,
  onClick,
}: ToggleProps) => (
  <Box
    component="button"
    onClick={onClick}
    aria-label={ariaLabel}
    aria-pressed={isOff}
    sx={{
      ...outlinedControlButtonSx,
      // The label stays the same and the state shows in the icon and fill, so
      // toggling never changes the button's width.
      minWidth: 132,
      bgcolor: isOff ? DS.surfaceTint : 'transparent',
    }}
  >
    {icon}
    {label}
  </Box>
);

type Props = {
  onEnd: () => void;
};

const ControlBar = ({ onEnd }: Props) => {
  const { t } = useTranslation();
  const { isMicrophoneEnabled, isCameraEnabled, localParticipant } =
    useLocalParticipant();

  const toggleMic = useCallback(() => {
    localParticipant
      .setMicrophoneEnabled(!isMicrophoneEnabled)
      .catch(reportToggleError);
  }, [localParticipant, isMicrophoneEnabled]);

  const toggleCamera = useCallback(() => {
    localParticipant
      .setCameraEnabled(!isCameraEnabled)
      .catch(reportToggleError);
  }, [localParticipant, isCameraEnabled]);

  return (
    <Box
      sx={{
        flex: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: '11px',
      }}
    >
      <ToggleButton
        icon={isMicrophoneEnabled ? <MicIcon /> : <MicOffIcon />}
        label={t('videoVisit.controls.mic')}
        ariaLabel={t(
          isMicrophoneEnabled
            ? 'videoVisit.a11y.micOff'
            : 'videoVisit.a11y.micOn'
        )}
        isOff={!isMicrophoneEnabled}
        onClick={toggleMic}
      />
      <ToggleButton
        icon={isCameraEnabled ? <VideocamIcon /> : <VideocamOffIcon />}
        label={t('videoVisit.controls.camera')}
        ariaLabel={t(
          isCameraEnabled
            ? 'videoVisit.a11y.cameraOff'
            : 'videoVisit.a11y.cameraOn'
        )}
        isOff={!isCameraEnabled}
        onClick={toggleCamera}
      />
      {/* Below lg the side panel, and the Byt enhet button in it, is hidden. */}
      <DeviceMenuButton
        opens="up"
        sx={{
          ...outlinedControlButtonSx,
          display: { xs: 'inline-flex', lg: 'none' },
        }}
      >
        <TuneIcon aria-hidden />
        {t('videoVisit.devices.switch')}
      </DeviceMenuButton>
      <Box
        component="button"
        onClick={onEnd}
        aria-label={t('videoVisit.a11y.leave')}
        sx={{
          ...controlButtonSx,
          px: '24px',
          border: 'none',
          bgcolor: DS.hangUp,
          color: DS.linen,
          '&:hover': { bgcolor: DS.hangUp, filter: 'brightness(0.96)' },
        }}
      >
        {t('videoVisit.controls.hangUp')}
      </Box>
    </Box>
  );
};

export default ControlBar;
